import styles from './Flow.detail.module.scss';
import { useHorizontalScroll, useIsPCOrMobile } from '@/hooks';
import { FlowPromiseModal, CourseDownloadButton } from './FlowDetail';
import { useState, useEffect } from 'react';
import { FileImage, HoverUnderlineWrapper, Underline, StickerNameRenderer } from '@/components';
import { cx } from '@emotion/css';
import { throttle } from 'lodash';
import { useRouter } from 'next/router';

const MAP_URL_PREFIX = '/static/images/flow/maps';

const ROUTE_INFORMATION = {
  family: {
    description: '가족과',
    mapURL: `${MAP_URL_PREFIX}/map_01_family.png`,
  },
  single: {
    description: '혼자서',
    mapURL: `${MAP_URL_PREFIX}/map_02_single.png`,
  },
  friends: {
    description: '여럿이서',
    mapURL: `${MAP_URL_PREFIX}/map_03_friends.png`,
  },
  double: {
    description: '둘이서',
    mapURL: `${MAP_URL_PREFIX}/map_04_double.png`,
  },
  hybrid: {
    description: '가고 싶은 대로 간다',
    mapURL: `${MAP_URL_PREFIX}/map_05_hybrid.png`,
  },
  dealer: {
    description: '끝까지 간다',
    mapURL: `${MAP_URL_PREFIX}/map_06_dealer.png`,
  },
  healer: {
    description: '너는 내가 살린다',
    mapURL: `${MAP_URL_PREFIX}/map_07_healer.png`,
  },
  tanker: {
    description: '나를 따르라',
    mapURL: `${MAP_URL_PREFIX}/map_08_tanker.png`,
  },
  n86: {
    description: 'PC통신과 라디오',
    mapURL: `${MAP_URL_PREFIX}/map_09_n86.png`,
  },
  x: {
    description: '삐삐와 워크맨',
    mapURL: `${MAP_URL_PREFIX}/map_10_X.png`,
  },
  m: {
    description: '피처폰과 MP3',
    mapURL: `${MAP_URL_PREFIX}/map_11_M.png`,
  },
  z: {
    description: '스마트폰과 유튜브',
    mapURL: `${MAP_URL_PREFIX}/map_12_Z.png`,
  },
};

const FlowDetail = ({ type, collections }) => {
  const router = useRouter();
  const { isPC } = useIsPCOrMobile({});
  const { wrapperRef, scrollRef } = useHorizontalScroll({
    condition: isPC,
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredCollection, setHoveredCollection] = useState(null);
  const pointColor = '#454de0';

  useEffect(() => {
    const listenScroll = throttle(() => {
      const scrollPosition = document.documentElement.scrollTop;
      setIsScrolled(scrollPosition > 0);
    }, 100);

    document.body.style.backgroundColor = '#222';
    window.addEventListener('scroll', listenScroll);
    return () => {
      document.body.style.backgroundColor = '';
      window.removeEventListener('scroll', listenScroll);
    };
  }, []);

  useEffect(() => {
    if (isScrolled && window.scrollY < 10) {
      window.scrollTo(0, 10);
    }
  }, [isScrolled]);

  useEffect(() => {
    const footer = document.getElementById('footer');
    if (isPC) {
      footer.style.display = 'none';
    }
    return () => {
      footer.style.display = '';
    };
  }, [isPC]);

  const onBoxHover = (collection) => {
    setHoveredCollection(isPC ? collection : false);
  };

  const openPopup = async (floor, index, collection) => {
    await FlowPromiseModal.open({
      collections,
      collection,
      openPopup,
      currentFloor: floor,
      currentIndex: index,
      pointColor,
    });
  };

  const goToFlowMain = () => {
    return router.push('/flow/main');
  };

  return (
    <div ref={wrapperRef} className={cx(styles.container, isScrolled && styles.scrolled)}>
      <div
        className={styles.floatingArea}
        style={{
          backgroundColor: pointColor,
        }}
      >
        <HoverUnderlineWrapper className={styles.otherCourseButton} isLink={true} underlineColor={'#fff'} height={'2'} onClick={goToFlowMain}>
          <Underline className={styles.text}>다른 루트 보기</Underline>
        </HoverUnderlineWrapper>
        <div className={styles.mainTitle}>
          <div className={styles.typeName}>
            <StickerNameRenderer type={type} subLanguageClassName={styles.typeKoreanName} />
          </div>
          <div> Route</div>
          <div className={styles.subTitle}>{ROUTE_INFORMATION[type].description}</div>
        </div>
      </div>
      <div className={styles.rightSide}>
        {hoveredCollection && (
          <div id="dimmed_text" className={styles.dimmedTitleContainer}>
            <div>
              <div className={styles.dimmedEngTitle}>{hoveredCollection.enCollectionName}</div>
              <div className={styles.dimmedKorTitle}>{hoveredCollection.koCollectionName}</div>
            </div>
          </div>
        )}
        <div ref={scrollRef} className={styles.scrollArea}>
          {Object.entries(collections.randomCollections).map(([floor, items]) => (
            <div key={floor} className={styles.floorSection}>
              <div className={styles.floorTitle}>{floor}</div>
              <div className={styles.boxContainer}>
                {items.map((item, index) => {
                  switch (item.type) {
                    case 'EMPTY':
                      return <div key={`EMPTY-${index}`} className={styles.box} />;
                    case 'FIXED':
                      return (
                        <div
                          key={item.id}
                          className={styles.box}
                          onClick={() => openPopup(floor, index, item)}
                          onMouseOver={() => onBoxHover(item)}
                          onMouseOut={() => onBoxHover(null)}
                        >
                          <div className={styles.boxImageContainer}>
                            <FileImage className={styles.fixedCollectionImage} src={item.image} alt={'collection'} />
                          </div>
                          <div className={styles.boxDescription}>
                            <div className={styles.boxTitle}>
                              <div className={styles.engTitle}>{item.enCollectionName}</div>
                              <div className={styles.korTitle}>{item.koCollectionName}</div>
                            </div>
                          </div>
                        </div>
                      );
                    case 'NOT_FIXED':
                      return (
                        <a
                          key={item.id}
                          className={`${styles.box} ${styles.boxHoverTransition}`}
                          onClick={() => openPopup(floor, index, item)}
                          onMouseOver={() => onBoxHover(item)}
                          onMouseOut={() => onBoxHover(null)}
                        >
                          <div className={styles.boxImageContainer}>
                            <FileImage className={styles.boxImage} src={item.image} alt={'collection'} />
                          </div>
                          <div className={styles.boxDescription}>
                            <div className={styles.boxTitle}>
                              <div className={styles.engTitle}>{item.enCollectionName}</div>
                              <div className={styles.korTitle}>{item.koCollectionName}</div>
                            </div>
                          </div>
                        </a>
                      );
                  }
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.fixedArea}>
        <CourseDownloadButton type={type} mapURL={ROUTE_INFORMATION[type].mapURL} />
      </div>
      {hoveredCollection && <div className={styles.dimmed} />}
    </div>
  );
};

export default FlowDetail;
